import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";
import { FileText, Download, Send, Loader2, Plus, X } from "lucide-react";
import { format } from "date-fns";
import { generateDocumentNumber } from "@/lib/documentNaming";
import { logActivity } from "@/lib/activityLogger";
import { generateAgreementPdf, pdfBlobToBase64 } from "@/lib/nativePdfGenerator";

interface InquiryData {
  id: string;
  name: string;
  email: string;
  phone: string;
  occasion?: string | null;
  venue_address: string;
  event_dates_json?: any;
  total_cost?: number | null;
  packages_json?: any;
}

interface ExistingContract {
  id: string;
  contract_number: string;
  total_amount: number | null;
  deposit_amount: number | null;
  deposit_due_date: string | null;
  final_payment_due_date: string | null;
  payment_mode: 'full' | 'deposit' | string | null;
  deposit_percentage: number | null;
  balance_amount: number | null;
  balance_due_date: string | null;
  balance_reminder_date: string | null;
  daily_late_fee: number | null;
  includes_airfare: boolean | null;
  includes_accommodation: boolean | null;
  includes_meals: boolean | null;
  includes_ground_transport: boolean | null;
  custom_inclusions: string[] | null;
  custom_exclusions: string[] | null;
  payment_terms: string | null;
  cancellation_policy: string | null;
  force_majeure_clause: string | null;
  liability_clause: string | null;
  special_requirements: string | null;
  additional_notes: string | null;
  storage_path: string | null;
}

interface ContractBuilderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inquiry: InquiryData;
  existingContract?: ExistingContract | null;
  onContractGenerated?: (pdf: { filename: string; content: string }) => void;
  onContractSaved?: () => void;
}

const DEFAULT_PAYMENT_TERMS_FULL = `1. Full payment is required to secure your booking date.
2. Payment is due within 7 days of contract signing.
3. Payment can be made via bank transfer, credit card, or check.
4. All prices are in USD unless otherwise specified.`;

const LEGACY_PAYMENT_TERMS_DEPOSIT = `1. A deposit is required within 14 days of contract signing to secure your booking date. If the deposit is not received within 14 days, this contract becomes void.
2. The remaining balance is due at least 30 days prior to the event date.
3. Late payments are subject to a daily late fee as specified in the payment schedule.
4. Payment can be made via bank transfer, credit card, or check.
5. All prices are in USD unless otherwise specified.`;

const DEFAULT_PAYMENT_TERMS_DEPOSIT = `1. A deposit is required within 14 days of contract signing to secure your booking date. If the deposit is not received within 14 days, this contract becomes void.
2. The remaining balance is due at least 30 days prior to the event date.
3. Late payments are subject to a daily late fee as specified in the payment schedule.
4. Payment can be made via bank transfer, credit card, or check.
5. All prices are in USD unless otherwise specified.`;

const DEFAULT_CANCELLATION_POLICY = `1. Cancellations made 60+ days before the event: Full refund minus $250 administrative fee.
2. Cancellations made 30-59 days before: 50% refund of total contract value.
3. Cancellations made 15-29 days before: 25% refund of total contract value.
4. Cancellations made less than 15 days before: No refund.
5. Rescheduling is subject to availability and may incur additional fees.`;

const DEFAULT_FORCE_MAJEURE = `Neither party shall be liable for failure to perform due to circumstances beyond their reasonable control, including but not limited to: natural disasters, pandemics, government actions, civil unrest, or other force majeure events. In such cases, the parties will work together in good faith to reschedule the event or arrange appropriate compensation.`;

const DEFAULT_LIABILITY = `The Lost Strings Company maintains comprehensive liability insurance. Our liability is limited to the total contract value. We are not responsible for venue-related issues, third-party equipment failures, or circumstances beyond our control. The Client agrees to provide a safe working environment for our musicians.`;

export function ContractBuilder({
  open,
  onOpenChange,
  inquiry,
  existingContract,
  onContractGenerated,
  onContractSaved,
}: ContractBuilderProps) {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingPrice, setEditingPrice] = useState<number | null>(null);
  const [priceTooLow, setPriceTooLow] = useState(false);
  const [priceApproved, setPriceApproved] = useState(false);

  // Contract fields
  const [contractNumber, setContractNumber] = useState("");
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [paymentDueDate, setPaymentDueDate] = useState("");

  // Payment mode
  const [paymentMode, setPaymentMode] = useState<'full' | 'deposit'>('full');
  const [depositType, setDepositType] = useState<'percentage' | 'fixed'>('percentage');
  const [depositPercentage, setDepositPercentage] = useState<number>(50);
  const [depositFixedAmount, setDepositFixedAmount] = useState<number>(0);
  const [balanceDueDate, setBalanceDueDate] = useState("");
  const [balanceReminderDate, setBalanceReminderDate] = useState("");
  const [dailyLateFee, setDailyLateFee] = useState<number>(0);

  const effectiveTotalAmount = editingPrice ?? totalAmount;
  const pendingPriceChanged = editingPrice !== null && editingPrice !== totalAmount;

  // Computed deposit/balance amounts
  const rawDepositAmount = paymentMode === 'deposit'
    ? (depositType === 'percentage' ? Math.round(effectiveTotalAmount * depositPercentage / 100) : depositFixedAmount)
    : effectiveTotalAmount;
  const computedDepositAmount = Math.max(0, Math.min(effectiveTotalAmount, rawDepositAmount));
  const computedBalanceAmount = paymentMode === 'deposit' ? Math.max(0, effectiveTotalAmount - computedDepositAmount) : 0;
  const effectiveDailyLateFee = paymentMode === 'deposit' ? Math.max(dailyLateFee || 0, 20) : 0;

  // Inclusions/Exclusions
  const [includesAirfare, setIncludesAirfare] = useState(false);
  const [includesAccommodation, setIncludesAccommodation] = useState(false);
  const [includesMeals, setIncludesMeals] = useState(false);
  const [includesGroundTransport, setIncludesGroundTransport] = useState(false);
  const [customInclusions, setCustomInclusions] = useState<string[]>([]);
  const [customExclusions, setCustomExclusions] = useState<string[]>([]);
  const [newInclusion, setNewInclusion] = useState("");
  const [newExclusion, setNewExclusion] = useState("");

  // Terms
  const [paymentTerms, setPaymentTerms] = useState(DEFAULT_PAYMENT_TERMS_FULL);
  const [cancellationPolicy, setCancellationPolicy] = useState(DEFAULT_CANCELLATION_POLICY);
  const [forceMajeureClause, setForceMajeureClause] = useState(DEFAULT_FORCE_MAJEURE);
  const [liabilityClause, setLiabilityClause] = useState(DEFAULT_LIABILITY);
  const [specialRequirements, setSpecialRequirements] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");

  useEffect(() => {
    if (open) {
      setEditingPrice(null);
      setPriceTooLow(false);
      setPriceApproved(false);

      if (existingContract) {
        // Load existing contract data for editing
        const existingPaymentMode = existingContract.payment_mode === 'deposit' ? 'deposit' : 'full';
        const hasStoredPercentage = typeof existingContract.deposit_percentage === 'number' && existingContract.deposit_percentage > 0;

        setContractNumber(existingContract.contract_number);
        setTotalAmount(existingContract.total_amount || 0);
        setPaymentMode(existingPaymentMode);
        setDepositType(hasStoredPercentage ? 'percentage' : 'fixed');
        setDepositPercentage(existingContract.deposit_percentage || 50);
        setDepositFixedAmount(hasStoredPercentage ? 0 : existingContract.deposit_amount || 0);
        setPaymentDueDate(existingContract.deposit_due_date || "");
        setBalanceDueDate(existingContract.balance_due_date || existingContract.final_payment_due_date || "");
        setBalanceReminderDate(existingContract.balance_reminder_date || "");
        setDailyLateFee(existingPaymentMode === 'deposit' ? Math.max(existingContract.daily_late_fee || 20, 20) : 0);
        setIncludesAirfare(existingContract.includes_airfare || false);
        setIncludesAccommodation(existingContract.includes_accommodation || false);
        setIncludesMeals(existingContract.includes_meals || false);
        setIncludesGroundTransport(existingContract.includes_ground_transport || false);
        setCustomInclusions(existingContract.custom_inclusions || []);
        setCustomExclusions(existingContract.custom_exclusions || []);
        setPaymentTerms(
          existingContract.payment_terms && existingContract.payment_terms !== LEGACY_PAYMENT_TERMS_DEPOSIT
            ? existingContract.payment_terms
            : (existingPaymentMode === 'deposit' ? DEFAULT_PAYMENT_TERMS_DEPOSIT : DEFAULT_PAYMENT_TERMS_FULL)
        );
        setCancellationPolicy(existingContract.cancellation_policy || DEFAULT_CANCELLATION_POLICY);
        setForceMajeureClause(existingContract.force_majeure_clause || DEFAULT_FORCE_MAJEURE);
        setLiabilityClause(existingContract.liability_clause || DEFAULT_LIABILITY);
        setSpecialRequirements(existingContract.special_requirements || "");
        setAdditionalNotes(existingContract.additional_notes || "");
      } else {
        // Generate new contract number using naming convention
        // Get first event date
        const firstEventDate = inquiry.event_dates_json?.[0]?.date || null;
        const num = generateDocumentNumber('Agreement', firstEventDate);
        setContractNumber(num);

        // Reset all fields to defaults
        setIncludesAirfare(false);
        setIncludesAccommodation(false);
        setIncludesMeals(false);
        setIncludesGroundTransport(false);
        setCustomInclusions([]);
        setCustomExclusions(["Sound setup and tech rider"]);
        setCancellationPolicy(DEFAULT_CANCELLATION_POLICY);
        setForceMajeureClause(DEFAULT_FORCE_MAJEURE);
        setLiabilityClause(DEFAULT_LIABILITY);
        setSpecialRequirements("");
        setAdditionalNotes("");
        setDepositType('percentage');
        setDepositPercentage(50);
        setDepositFixedAmount(0);
        setBalanceDueDate("");
        setBalanceReminderDate("");
        setDailyLateFee(0);

        // Set full payment amount
        if (inquiry.total_cost) {
          setTotalAmount(inquiry.total_cost);
        }

        // Auto-detect payment mode based on 30-day threshold from first event
        const eventDateForDue = inquiry.event_dates_json?.[0]?.date;
        if (eventDateForDue) {
          try {
            const eventDate = new Date(eventDateForDue);
            const now = new Date();
            const daysUntilEvent = Math.floor((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

            if (daysUntilEvent <= 30) {
              // Event is within 30 days — force full payment
              setPaymentMode('full');
              setPaymentTerms(DEFAULT_PAYMENT_TERMS_FULL);
              const depositDue = new Date();
              depositDue.setDate(depositDue.getDate() + 7);
              setPaymentDueDate(format(depositDue, "yyyy-MM-dd"));
            } else {
              // Event is more than 30 days away — default to deposit mode
              setPaymentMode('deposit');
              setPaymentTerms(DEFAULT_PAYMENT_TERMS_DEPOSIT);
              setDailyLateFee(20); // Minimum $20/day default
              const depositDue = new Date();
              depositDue.setDate(depositDue.getDate() + 7);
              setPaymentDueDate(format(depositDue, "yyyy-MM-dd"));

              // Balance due date: 30 days before first event
              const balanceDue = new Date(eventDate);
              balanceDue.setDate(balanceDue.getDate() - 30);
              setBalanceDueDate(format(balanceDue, "yyyy-MM-dd"));

              // Reminder: 7 days before balance due
              const reminderDate = new Date(balanceDue);
              reminderDate.setDate(reminderDate.getDate() - 7);
              setBalanceReminderDate(format(reminderDate, "yyyy-MM-dd"));
            }
          } catch {
            setPaymentMode('full');
            setPaymentTerms(DEFAULT_PAYMENT_TERMS_FULL);
          }
        } else {
          setPaymentMode('full');
          setPaymentTerms(DEFAULT_PAYMENT_TERMS_FULL);
        }
      }
    }
  }, [open, inquiry, existingContract]);

  const addInclusion = () => {
    if (newInclusion.trim()) {
      setCustomInclusions([...customInclusions, newInclusion.trim()]);
      setNewInclusion("");
    }
  };

  const addExclusion = () => {
    if (newExclusion.trim()) {
      setCustomExclusions([...customExclusions, newExclusion.trim()]);
      setNewExclusion("");
    }
  };

  const generateContractHtml = (): string => {
    const normalizeInstrumentList = (value: any): string[] => {
      if (!value) return [];
      if (Array.isArray(value)) {
        return value
          .flatMap((item) => {
            if (typeof item === 'string' && item.includes(',')) {
              return item.split(',').map((s) => s.trim()).filter(Boolean);
            }
            return item;
          })
          .filter(Boolean);
      }
      if (typeof value === 'string') {
        return value.split(',').map((s) => s.trim()).filter(Boolean);
      }
      return [];
    };

    const eventDetails = inquiry.event_dates_json?.map((evt: any, idx: number) => {
      const pkg = inquiry.packages_json?.find((p: any) => p.eventIndex === idx);
      const finalInstruments = normalizeInstrumentList(evt.finalInstruments);
      return `
        <div class="event-block">
          <h4>Event ${idx + 1}: ${evt.occasion || 'Event'}</h4>
          <p><strong>Date:</strong> ${evt.date}</p>
          <p><strong>Time:</strong> ${evt.startTime} - ${evt.endTime}</p>
          <p><strong>Venue:</strong> ${evt.venueAddress || inquiry.venue_address}</p>
          ${evt.guestCount ? `<p><strong>Guest Count:</strong> ${evt.guestCount}</p>` : ''}
          ${pkg ? `<p><strong>Package:</strong> ${pkg.name} - $${pkg.price?.toLocaleString()}</p>` : ''}
          ${finalInstruments.length > 0 ? `
            <p><strong>Ensemble:</strong> ${finalInstruments.join(', ')}</p>
          ` : ''}
        </div>
      `;
    }).join('') || `
      <div class="event-block">
        <p><strong>Occasion:</strong> ${inquiry.occasion || 'Event'}</p>
        <p><strong>Venue:</strong> ${inquiry.venue_address}</p>
      </div>
    `;

    const depositDueLabel = paymentDueDate ? format(new Date(paymentDueDate), 'MMMM d, yyyy') : 'Upon signing';
    const voidDate = paymentDueDate ? new Date(paymentDueDate) : null;
    if (voidDate) voidDate.setDate(voidDate.getDate() + 1);
    const voidDateLabel = voidDate ? format(voidDate, 'MMMM d, yyyy') : depositDueLabel;
    const balanceDueLabel = balanceDueDate ? format(new Date(balanceDueDate), 'MMMM d, yyyy') : 'TBD';
    const reminderDateLabel = balanceReminderDate ? format(new Date(balanceReminderDate), 'MMMM d, yyyy') : null;

    const inclusionsList = [
      ...(includesAirfare ? ['Airfare for musicians/team'] : []),
      ...(includesAccommodation ? ['Accommodation'] : []),
      ...(includesMeals ? ['Meals and refreshments'] : []),
      ...(includesGroundTransport ? ['Ground transportation'] : []),
      ...customInclusions,
    ];

    const exclusionsList = [
      ...(!includesAirfare ? ['Airfare'] : []),
      ...(!includesAccommodation ? ['Accommodation'] : []),
      ...(!includesMeals ? ['Meals'] : []),
      ...(!includesGroundTransport ? ['Ground transportation'] : []),
      ...customExclusions,
    ];

    return `
<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: 'Georgia', serif; max-width: 800px; margin: 0 auto; padding: 40px; color: #333; }
    .header { text-align: center; border-bottom: 2px solid #8B4513; padding-bottom: 20px; margin-bottom: 30px; }
    .header h1 { color: #8B4513; margin-bottom: 5px; }
    .header p { color: #666; font-size: 14px; }
    .section { margin-bottom: 25px; }
    .section h2 { color: #8B4513; font-size: 18px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
    .section h3 { color: #555; font-size: 16px; margin-top: 15px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
    .info-item { padding: 8px 0; }
    .info-item strong { color: #555; }
    .event-block { background: #f9f9f9; padding: 15px; border-left: 3px solid #8B4513; margin: 10px 0; }
    .event-block h4 { margin: 0 0 10px 0; color: #8B4513; }
    .event-block p { margin: 5px 0; font-size: 14px; }
    .terms { font-size: 13px; line-height: 1.6; }
    .terms ol, .terms ul { padding-left: 20px; }
    .total { font-size: 20px; color: #8B4513; font-weight: bold; text-align: right; padding: 15px; background: #f9f9f9; border: 2px solid #8B4513; margin: 20px 0; }
    .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 50px; }
    .signature-block { border-top: 1px solid #333; padding-top: 10px; }
    .signature-block p { margin: 5px 0; font-size: 12px; }
    .footer { text-align: center; font-size: 11px; color: #999; margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; }
    .inclusions-exclusions { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
    .list-section ul { list-style: none; padding: 0; }
    .list-section li { padding: 3px 0; font-size: 13px; }
    .list-section li:before { content: '✓ '; color: #28a745; }
    .list-section.exclusions li:before { content: '✗ '; color: #dc3545; }
  </style>
</head>
<body>
  <div class="header">
    <h1>THE LOST STRINGS COMPANY</h1>
    <p>Live Music Services Agreement</p>
    <p>Contract #: ${contractNumber}</p>
  </div>

  <div class="section">
    <h2>Client Information</h2>
    <div class="info-grid">
      <div class="info-item"><strong>Name:</strong> ${inquiry.name}</div>
      <div class="info-item"><strong>Email:</strong> ${inquiry.email}</div>
      <div class="info-item"><strong>Phone:</strong> ${inquiry.phone}</div>
      <div class="info-item"><strong>Contract Date:</strong> ${format(new Date(), 'MMMM d, yyyy')}</div>
    </div>
  </div>

  <div class="section">
    <h2>Event Details</h2>
    ${eventDetails}
  </div>

  <div class="section">
    <h2>Services & Ensemble Details</h2>
    ${inquiry.event_dates_json?.map((evt: any, idx: number) => {
      const pkg = inquiry.packages_json?.find((p: any) => p.eventIndex === idx);
      const finalInstruments = normalizeInstrumentList(evt.finalInstruments);
      
      // Use final instruments if set, otherwise fall back to package musicians
      const displayInstruments = finalInstruments.length > 0 
        ? finalInstruments 
        : normalizeInstrumentList(pkg?.musicians);
      
      return `
        <div class="event-block">
          <h4>Event ${idx + 1}: ${evt.occasion || 'Event'} - ${evt.date}</h4>
          ${pkg ? `<p><strong>Package:</strong> ${pkg.name}</p>` : ''}
          ${displayInstruments.length > 0 ? `
            <p><strong>Musicians:</strong></p>
            <ul style="margin: 5px 0; padding-left: 20px;">
              ${displayInstruments.map((inst: string) => `<li style="margin: 2px 0;">${inst}</li>`).join('')}
            </ul>
          ` : ''}
          ${pkg?.price ? `<p><strong>Price:</strong> $${pkg.price.toLocaleString()}</p>` : ''}
        </div>
      `;
    }).join('') || (inquiry.packages_json?.map((pkg: any) => `
      <div class="event-block">
        <h4>${pkg.name}</h4>
        <p>${Array.isArray(pkg.musicians) ? pkg.musicians.join(', ') : pkg.musicians || ''}</p>
        <p><strong>Price:</strong> $${pkg.price?.toLocaleString()}</p>
      </div>
    `).join('') || '<p>Services as discussed</p>')}
    <div class="total">
      ${paymentMode === 'deposit' ? `
        <div style="text-align: left; font-size: 14px; line-height: 1.7; color: #333;">
          <div style="font-size: 20px; color: #8B4513; font-weight: bold; margin-bottom: 8px; text-align: right;">
            Total Contract Value: $${effectiveTotalAmount.toLocaleString()}
          </div>
          <div><strong>Initial Deposit at Signature:</strong> $${computedDepositAmount.toLocaleString()}${depositType === 'percentage' ? ` (${depositPercentage}%)` : ''}</div>
          <div><strong>Initial Deposit Due:</strong> ${depositDueLabel}</div>
          <div><strong>Remaining Balance:</strong> $${computedBalanceAmount.toLocaleString()}${balanceDueDate ? ` due ${balanceDueLabel}` : ''}</div>
          <div><strong>Agreement Void Date:</strong> ${voidDateLabel} if the initial deposit is unpaid</div>
          ${reminderDateLabel ? `<div><strong>Reminder Email Date:</strong> ${reminderDateLabel}</div>` : ''}
          <div><strong>Late Fee:</strong> $${effectiveDailyLateFee.toLocaleString()} per day after the balance due date</div>
        </div>
      ` : `
        Total Contract Value: $${effectiveTotalAmount.toLocaleString()}
      `}
    </div>
  </div>

  <div class="section">
    <h2>What's Included / Not Included</h2>
    <div class="inclusions-exclusions">
      <div class="list-section">
        <h3>Included:</h3>
        <ul>
          ${inclusionsList.map(item => `<li>${item}</li>`).join('') || '<li>As per package description</li>'}
        </ul>
      </div>
      <div class="list-section exclusions">
        <h3>Not Included (Client Responsibility):</h3>
        <ul>
          ${exclusionsList.map(item => `<li>${item}</li>`).join('') || '<li>None specified</li>'}
        </ul>
      </div>
    </div>
  </div>

  <div class="section">
    <h2>Payment Schedule</h2>
    ${paymentMode === 'deposit' ? `
    <div class="info-grid">
      <div class="info-item"><strong>Deposit Amount:</strong> $${computedDepositAmount.toLocaleString()} (${depositType === 'percentage' ? depositPercentage + '%' : 'fixed'})</div>
      <div class="info-item"><strong>Deposit Due:</strong> ${depositDueLabel}</div>
      <div class="info-item"><strong>Remaining Balance:</strong> $${computedBalanceAmount.toLocaleString()}</div>
      <div class="info-item"><strong>Balance Due:</strong> ${balanceDueLabel}</div>
      <div class="info-item"><strong>Agreement Void Date:</strong> ${voidDateLabel}</div>
      ${reminderDateLabel ? `<div class="info-item"><strong>Reminder Email Date:</strong> ${reminderDateLabel}</div>` : ''}
      <div class="info-item"><strong>Daily Late Fee:</strong> $${effectiveDailyLateFee.toLocaleString()} / day</div>
    </div>
    <p style="margin-top: 10px; font-size: 13px; color: #666;">Late fee of $${effectiveDailyLateFee.toLocaleString()} per day will be applied to the total overdue balance for payments received after the balance due date.</p>
    <p style="margin-top: 10px; font-size: 13px; color: #666;">If the initial deposit is not received by ${depositDueLabel}, this agreement becomes void on ${voidDateLabel}.</p>
    ` : `
    <div class="info-grid">
      <div class="info-item"><strong>Payment Amount:</strong> $${effectiveTotalAmount.toLocaleString()}</div>
      <div class="info-item"><strong>Payment Due:</strong> ${paymentDueDate ? format(new Date(paymentDueDate), 'MMMM d, yyyy') : 'TBD'}</div>
    </div>
    <p style="margin-top: 10px; font-size: 13px; color: #666;">One-time payment required to confirm booking.</p>
    `}
  </div>

  <div class="section terms">
    <h2>Payment Terms</h2>
    <p style="white-space: pre-wrap;">${paymentTerms}</p>
  </div>

  <div class="section terms">
    <h2>Cancellation & Refund Policy</h2>
    <p style="white-space: pre-wrap;">${cancellationPolicy}</p>
  </div>

  <div class="section terms">
    <h2>Force Majeure</h2>
    <p style="white-space: pre-wrap;">${forceMajeureClause}</p>
  </div>

  <div class="section terms">
    <h2>Liability & Insurance</h2>
    <p style="white-space: pre-wrap;">${liabilityClause}</p>
  </div>

  ${specialRequirements ? `
  <div class="section terms">
    <h2>Special Requirements</h2>
    <p style="white-space: pre-wrap;">${specialRequirements}</p>
  </div>
  ` : ''}

  ${additionalNotes ? `
  <div class="section terms">
    <h2>Additional Notes</h2>
    <p style="white-space: pre-wrap;">${additionalNotes}</p>
  </div>
  ` : ''}

  <div class="signatures">
    <div class="signature-block">
      <p><strong>Client Signature</strong></p>
      <p>Name: ${inquiry.name}</p>
      <p>Date: _________________</p>
      <p>Signature: _________________</p>
    </div>
    <div class="signature-block">
      <p><strong>The Lost Strings Company</strong></p>
      <p>Name: _________________</p>
      <p>Date: _________________</p>
      <p>Signature: _________________</p>
    </div>
  </div>

  <div class="footer">
    <p>The Lost Strings Company | Live Music for Unforgettable Moments</p>
    <p>This contract is binding upon signature by both parties.</p>
  </div>
</body>
</html>
    `;
  };

  const handleSaveContract = async () => {
    // Validate price floor (skip if approved override)
    const minPrice = (inquiry.total_cost || 0) * 0.85;
    if (effectiveTotalAmount < minPrice && !priceApproved) {
      toast({
        title: "Price too low",
        description: `Use "Send for Approval" to override the 15% floor`,
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Build agreement data for native PDF generation
      const normalizeInstrumentList = (value: any): string[] => {
        if (!value) return [];
        if (Array.isArray(value)) {
          return value
            .flatMap((item) => {
              if (typeof item === 'string' && item.includes(',')) {
                return item.split(',').map((s) => s.trim()).filter(Boolean);
              }
              return item;
            })
            .filter(Boolean);
        }
        if (typeof value === 'string') {
          return value.split(',').map((s) => s.trim()).filter(Boolean);
        }
        return [];
      };

      const events = (inquiry.event_dates_json || []).map((evt: any, idx: number) => {
        const pkg = inquiry.packages_json?.find((p: any) => p.eventIndex === idx);
        const finalInstruments = normalizeInstrumentList(evt.finalInstruments);
        const displayInstruments = finalInstruments.length > 0 
          ? finalInstruments 
          : normalizeInstrumentList(pkg?.musicians);
        
        return {
          date: evt.date || '',
          occasion: evt.occasion || 'Event',
          startTime: evt.startTime || '',
          endTime: evt.endTime || '',
          venueAddress: evt.venueAddress || inquiry.venue_address,
          guestCount: evt.guestCount,
          packageName: pkg?.name,
          packagePrice: pkg?.price,
          instruments: displayInstruments,
        };
      });

      const inclusionsList = [
        ...(includesAirfare ? ['Airfare for musicians/team'] : []),
        ...(includesAccommodation ? ['Accommodation'] : []),
        ...(includesMeals ? ['Meals and refreshments'] : []),
        ...(includesGroundTransport ? ['Ground transportation'] : []),
        ...customInclusions,
      ];

      const exclusionsList = [
        ...(!includesAirfare ? ['Airfare'] : []),
        ...(!includesAccommodation ? ['Accommodation'] : []),
        ...(!includesMeals ? ['Meals'] : []),
        ...(!includesGroundTransport ? ['Ground transportation'] : []),
        ...customExclusions,
      ];

      const agreementData = {
        contractNumber,
        clientName: inquiry.name,
        clientEmail: inquiry.email,
        clientPhone: inquiry.phone,
        events,
        totalAmount: effectiveTotalAmount,
        depositAmount: computedDepositAmount,
        depositDueDate: paymentDueDate,
        paymentMode,
        ...(paymentMode === 'deposit' ? {
          balanceAmount: computedBalanceAmount,
          balanceDueDate,
          balanceReminderDate,
          dailyLateFee: effectiveDailyLateFee,
          depositPercentage: depositType === 'percentage' ? depositPercentage : undefined,
        } : {}),
        inclusions: inclusionsList,
        exclusions: exclusionsList,
        paymentTerms,
        cancellationPolicy,
        forceMajeureClause,
        liabilityClause,
        specialRequirements,
        additionalNotes,
      };

      // Generate native PDF
      const pdfBlob = generateAgreementPdf(agreementData);
      const storagePath = `${inquiry.id}/${contractNumber}.pdf`;
      
      const { error: uploadError } = await supabase.storage
        .from('contracts')
        .upload(storagePath, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true,
        });

      if (uploadError) {
        console.error("Storage upload error:", uploadError);
        throw uploadError;
      }

      const contractData: Record<string, any> = {
        total_amount: effectiveTotalAmount,
        deposit_amount: computedDepositAmount,
        deposit_due_date: paymentDueDate || null,
        final_payment_due_date: paymentMode === 'deposit' ? balanceDueDate || null : null,
        payment_mode: paymentMode,
        deposit_percentage: paymentMode === 'deposit' && depositType === 'percentage' ? depositPercentage : null,
        balance_amount: paymentMode === 'deposit' ? computedBalanceAmount : null,
        balance_due_date: paymentMode === 'deposit' ? balanceDueDate || null : null,
        balance_reminder_date: paymentMode === 'deposit' ? balanceReminderDate || null : null,
        daily_late_fee: paymentMode === 'deposit' ? effectiveDailyLateFee : 0,
        includes_airfare: includesAirfare,
        includes_accommodation: includesAccommodation,
        includes_meals: includesMeals,
        includes_ground_transport: includesGroundTransport,
        custom_inclusions: customInclusions,
        custom_exclusions: customExclusions,
        payment_terms: paymentTerms,
        cancellation_policy: cancellationPolicy,
        force_majeure_clause: forceMajeureClause,
        liability_clause: liabilityClause,
        special_requirements: specialRequirements,
        additional_notes: additionalNotes,
        storage_path: storagePath,
      };

      let error;
      if (existingContract) {
        // Update existing contract - include updated_at
        const result = await supabase
          .from("inquiry_contracts")
          .update({
            ...contractData,
            updated_at: new Date().toISOString(),
          })
          .eq("id", existingContract.id);
        error = result.error;
      } else {
        // Insert new contract
        // Add eventDate and occasion to event_details for document grouping
        const firstEvent = inquiry.event_dates_json?.[0] || {};
        const firstEventDate = firstEvent.date || null;
        const occasion = firstEvent.occasion || inquiry.occasion || 'Event';
        
        const eventDetailsWithDate = {
          events: inquiry.event_dates_json || [],
          eventDate: firstEventDate,
          occasion: occasion,
          type: 'agreement',
        };
        
        const result = await supabase
          .from("inquiry_contracts")
          .insert({
            ...contractData,
            inquiry_id: inquiry.id,
            created_by: user?.id,
            contract_number: contractNumber,
            client_name: inquiry.name,
            client_email: inquiry.email,
            event_details: eventDetailsWithDate,
            packages: inquiry.packages_json || [],
            total_amount: effectiveTotalAmount,
            status: 'draft',
          });
        error = result.error;
      }

      if (error) throw error;

      if (pendingPriceChanged) {
        setTotalAmount(effectiveTotalAmount);
        setEditingPrice(null);
      }

      // Log activity (hidden from UI but stored for audit)
      logActivity({
        inquiryId: inquiry.id,
        action: existingContract ? 'document_edited' : 'document_created',
        description: existingContract 
          ? `Contract ${contractNumber} edited` 
          : `Contract ${contractNumber} created`,
        metadata: { contractNumber, isEdit: !!existingContract }
      });

      toast({
        title: existingContract ? "Contract updated ✓" : "Contract saved ✓",
        description: `Contract ${contractNumber} ${existingContract ? 'updated' : 'saved'} as PDF`,
      });
      
      // Notify parent to refresh contracts list
      onContractSaved?.();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error saving contract:", error);
      toast({
        title: "Failed to save contract",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleGeneratePdf = async () => {
    setIsGenerating(true);
    try {
      const filename = `Contract-${contractNumber}.pdf`;
      
      // Build agreement data (same as handleSaveContract)
      const normalizeInstrumentList = (value: any): string[] => {
        if (!value) return [];
        if (Array.isArray(value)) {
          return value
            .flatMap((item) => {
              if (typeof item === 'string' && item.includes(',')) {
                return item.split(',').map((s) => s.trim()).filter(Boolean);
              }
              return item;
            })
            .filter(Boolean);
        }
        if (typeof value === 'string') {
          return value.split(',').map((s) => s.trim()).filter(Boolean);
        }
        return [];
      };

      const events = (inquiry.event_dates_json || []).map((evt: any, idx: number) => {
        const pkg = inquiry.packages_json?.find((p: any) => p.eventIndex === idx);
        const finalInstruments = normalizeInstrumentList(evt.finalInstruments);
        const displayInstruments = finalInstruments.length > 0 
          ? finalInstruments 
          : normalizeInstrumentList(pkg?.musicians);
        
        return {
          date: evt.date || '',
          occasion: evt.occasion || 'Event',
          startTime: evt.startTime || '',
          endTime: evt.endTime || '',
          venueAddress: evt.venueAddress || inquiry.venue_address,
          guestCount: evt.guestCount,
          packageName: pkg?.name,
          packagePrice: pkg?.price,
          instruments: displayInstruments,
        };
      });

      const inclusionsList = [
        ...(includesAirfare ? ['Airfare for musicians/team'] : []),
        ...(includesAccommodation ? ['Accommodation'] : []),
        ...(includesMeals ? ['Meals and refreshments'] : []),
        ...(includesGroundTransport ? ['Ground transportation'] : []),
        ...customInclusions,
      ];

      const exclusionsList = [
        ...(!includesAirfare ? ['Airfare'] : []),
        ...(!includesAccommodation ? ['Accommodation'] : []),
        ...(!includesMeals ? ['Meals'] : []),
        ...(!includesGroundTransport ? ['Ground transportation'] : []),
        ...customExclusions,
      ];

      const agreementData = {
        contractNumber,
        clientName: inquiry.name,
        clientEmail: inquiry.email,
        clientPhone: inquiry.phone,
        events,
        totalAmount,
        depositAmount: paymentMode === 'deposit' ? computedDepositAmount : totalAmount,
        depositDueDate: paymentDueDate,
        paymentMode,
        ...(paymentMode === 'deposit' ? {
          balanceAmount: computedBalanceAmount,
          balanceDueDate,
          balanceReminderDate,
          dailyLateFee,
          depositPercentage: depositType === 'percentage' ? depositPercentage : undefined,
        } : {}),
        inclusions: inclusionsList,
        exclusions: exclusionsList,
        paymentTerms,
        cancellationPolicy,
        forceMajeureClause,
        liabilityClause,
        specialRequirements,
        additionalNotes,
      };

      // Generate native PDF
      const pdfBlob = generateAgreementPdf(agreementData);
      const storagePath = `${inquiry.id}/${contractNumber}.pdf`;
      
      // Upload to storage
      await supabase.storage
        .from('contracts')
        .upload(storagePath, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true,
        });
      
      // Get base64 for email attachment
      const base64 = await pdfBlobToBase64(pdfBlob);
      
      // Trigger download
      const url = URL.createObjectURL(pdfBlob);
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = filename;
      downloadLink.click();
      URL.revokeObjectURL(url);

      // Pass to parent for email attachment
      onContractGenerated?.({
        filename,
        content: base64,
      });

      toast({
        title: "Contract PDF generated ✓",
        description: "Contract saved to storage and downloaded as PDF",
      });
    } catch (error: any) {
      console.error("Error generating contract:", error);
      toast({
        title: "Failed to generate contract",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {existingContract ? 'Edit Contract' : 'Contract Builder'}
          </DialogTitle>
          <DialogDescription>
            {existingContract 
              ? `Editing contract ${existingContract.contract_number} for ${inquiry.name}`
              : `Create a contract for ${inquiry.name} - ${inquiry.occasion || 'Event'}`
            }
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6 py-4">
            {/* Contract Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contract Number</Label>
                <Input value={contractNumber} onChange={(e) => setContractNumber(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Total Amount (Original: ${inquiry.total_cost?.toLocaleString()})</Label>
                <div className="relative flex gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-2.5 text-sm text-foreground">$</span>
                    <Input 
                      type="number" 
                       value={editingPrice ?? totalAmount} 
                      onChange={(e) => setEditingPrice(Number(e.target.value))}
                      className="pl-6 text-foreground bg-background"
                    />
                  </div>
                   {pendingPriceChanged && (
                    <Button 
                      size="sm" 
                      onClick={() => {
                        const minAllowed = (inquiry.total_cost || 0) * 0.85;
                        if (editingPrice < minAllowed) {
                          setPriceTooLow(true);
                        } else {
                          setTotalAmount(editingPrice);
                          setEditingPrice(null);
                          setPriceTooLow(false);
                        }
                      }}
                    >
                      Apply New Price
                    </Button>
                  )}
                </div>
                {priceTooLow && (
                  <div className="space-y-2 p-3 rounded-md border border-destructive bg-destructive/10">
                    <p className="text-[11px] text-destructive font-medium">
                      ⚠️ Price is more than 15% below original (min ${((inquiry.total_cost || 0) * 0.85).toLocaleString()}). This requires approval.
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => {
                          setEditingPrice(null);
                          setPriceTooLow(false);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        size="sm" 
                        variant="default"
                        onClick={() => {
                          setTotalAmount(editingPrice!);
                          setEditingPrice(null);
                          setPriceTooLow(false);
                          setPriceApproved(true);
                          toast({
                            title: "Reduced price approved ✓",
                            description: `Contract price set to $${editingPrice!.toLocaleString()} (approved override)`,
                          });
                        }}
                      >
                        <Send className="h-3 w-3 mr-1" />
                        Send for Approval
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Payment Schedule */}
            <Accordion type="single" collapsible defaultValue="payment">
              <AccordionItem value="payment">
                <AccordionTrigger>Payment Schedule</AccordionTrigger>
                <AccordionContent>
                  {/* Payment Mode Toggle */}
                  <div className="flex gap-2 mb-4">
                    <Button
                      size="sm"
                      variant={paymentMode === 'full' ? 'default' : 'outline'}
                      onClick={() => {
                        setPaymentMode('full');
                        setPaymentTerms(DEFAULT_PAYMENT_TERMS_FULL);
                      }}
                    >
                      Full Payment
                    </Button>
                    <Button
                      size="sm"
                      variant={paymentMode === 'deposit' ? 'default' : 'outline'}
                      onClick={() => {
                        setPaymentMode('deposit');
                        setPaymentTerms(DEFAULT_PAYMENT_TERMS_DEPOSIT);
                      }}
                    >
                      Deposit + Balance
                    </Button>
                  </div>

                  {paymentMode === 'full' ? (
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div className="space-y-2">
                          <Label>Total Contract Amount</Label>
                          <Input value={`$${effectiveTotalAmount.toLocaleString()}`} disabled />
                      </div>
                      <div className="space-y-2">
                        <Label>Payment Due Date</Label>
                        <Input type="date" value={paymentDueDate} onChange={(e) => setPaymentDueDate(e.target.value)} />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4 pt-2">
                      {/* Deposit Amount */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Deposit Amount</Label>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant={depositType === 'percentage' ? 'default' : 'outline'}
                              onClick={() => setDepositType('percentage')}
                              className="text-xs px-2"
                            >
                              %
                            </Button>
                            <Button
                              size="sm"
                              variant={depositType === 'fixed' ? 'default' : 'outline'}
                              onClick={() => setDepositType('fixed')}
                              className="text-xs px-2"
                            >
                              $
                            </Button>
                            {depositType === 'percentage' ? (
                              <div className="relative flex-1">
                                <Input
                                  type="number"
                                  value={depositPercentage}
                                  onChange={(e) => setDepositPercentage(Number(e.target.value))}
                                  min={1}
                                  max={99}
                                  className="pr-8"
                                />
                                <span className="absolute right-3 top-2.5 text-sm text-muted-foreground">%</span>
                              </div>
                            ) : (
                              <div className="relative flex-1">
                                <span className="absolute left-3 top-2.5 text-sm text-foreground">$</span>
                                <Input
                                  type="number"
                                  value={depositFixedAmount}
                                  onChange={(e) => setDepositFixedAmount(Number(e.target.value))}
                                  className="pl-6"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Deposit Due Date</Label>
                          <Input type="date" value={paymentDueDate} onChange={(e) => setPaymentDueDate(e.target.value)} />
                          <p className="text-[10px] text-muted-foreground">Contract voids if unpaid within 14 days</p>
                        </div>
                      </div>

                      {/* Calculated Summary */}
                      <div className="bg-muted/50 p-3 rounded-lg space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Deposit</span>
                          <span className="font-medium">${computedDepositAmount.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Remaining Balance</span>
                          <span className="font-medium">${computedBalanceAmount.toLocaleString()}</span>
                        </div>
                        <Separator className="my-1" />
                        <div className="flex justify-between font-semibold">
                          <span>Total</span>
                           <span>${effectiveTotalAmount.toLocaleString()}</span>
                        </div>
                      </div>

                      {/* Balance Due & Reminders */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Balance Due Date</Label>
                          <Input type="date" value={balanceDueDate} onChange={(e) => setBalanceDueDate(e.target.value)} />
                          <p className="text-[10px] text-muted-foreground">Default: 30 days before event</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Send Reminder On</Label>
                          <Input type="date" value={balanceReminderDate} onChange={(e) => setBalanceReminderDate(e.target.value)} />
                          <p className="text-[10px] text-muted-foreground">First balance payment email</p>
                        </div>
                      </div>

                      {/* Late Fee */}
                      <div className="space-y-2">
                        <Label>Daily Late Fee (after balance due date)</Label>
                        <div className="relative w-40">
                          <span className="absolute left-3 top-2.5 text-sm text-foreground">$</span>
                          <Input
                            type="number"
                            value={dailyLateFee}
                            onChange={(e) => {
                              const val = Number(e.target.value);
                              setDailyLateFee(val < 20 && val !== 0 ? 20 : val);
                            }}
                            onBlur={() => {
                              if (dailyLateFee > 0 && dailyLateFee < 20) setDailyLateFee(20);
                            }}
                            className="pl-6"
                            min={20}
                          />
                        </div>
                        <p className="text-[10px] text-muted-foreground">Minimum $20/day. Applied daily to balance if past due.</p>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground mt-2">
                    {paymentMode === 'full' 
                      ? '* One-time payment for the full amount.' 
                      : '* Deposit collected at signing. Balance collected separately via email.'}
                  </p>
                </AccordionContent>
              </AccordionItem>

              {/* Inclusions/Exclusions */}
              <AccordionItem value="inclusions">
                <AccordionTrigger>Inclusions & Exclusions</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Vendor Expenses Included:</Label>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Checkbox checked={includesAirfare} onCheckedChange={(c) => setIncludesAirfare(!!c)} />
                            <span className="text-sm">Airfare</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox checked={includesAccommodation} onCheckedChange={(c) => setIncludesAccommodation(!!c)} />
                            <span className="text-sm">Accommodation</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox checked={includesMeals} onCheckedChange={(c) => setIncludesMeals(!!c)} />
                            <span className="text-sm">Meals</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox checked={includesGroundTransport} onCheckedChange={(c) => setIncludesGroundTransport(!!c)} />
                            <span className="text-sm">Ground Transport</span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Custom Items:</Label>
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <Input 
                              placeholder="Add inclusion..." 
                              value={newInclusion}
                              onChange={(e) => setNewInclusion(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && addInclusion()}
                            />
                            <Button size="sm" onClick={addInclusion}><Plus className="h-4 w-4" /></Button>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {customInclusions.map((item, idx) => (
                              <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                                ✓ {item}
                                <X className="h-3 w-3 cursor-pointer" onClick={() => setCustomInclusions(prev => prev.filter((_, i) => i !== idx))} />
                              </Badge>
                            ))}
                          </div>
                          
                          <div className="flex gap-2 mt-2">
                            <Input 
                              placeholder="Add exclusion..." 
                              value={newExclusion}
                              onChange={(e) => setNewExclusion(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && addExclusion()}
                            />
                            <Button size="sm" variant="outline" onClick={addExclusion}><Plus className="h-4 w-4" /></Button>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {customExclusions.map((item, idx) => (
                              <Badge key={idx} variant="destructive" className="flex items-center gap-1">
                                ✗ {item}
                                <X className="h-3 w-3 cursor-pointer" onClick={() => setCustomExclusions(prev => prev.filter((_, i) => i !== idx))} />
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Legal Terms */}
              <AccordionItem value="terms">
                <AccordionTrigger>Legal Terms & Clauses</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-2">
                    <div className="space-y-2">
                      <Label>Payment Terms</Label>
                      <Textarea value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} rows={5} />
                    </div>
                    <div className="space-y-2">
                      <Label>Cancellation Policy</Label>
                      <Textarea value={cancellationPolicy} onChange={(e) => setCancellationPolicy(e.target.value)} rows={5} />
                    </div>
                    <div className="space-y-2">
                      <Label>Force Majeure Clause</Label>
                      <Textarea value={forceMajeureClause} onChange={(e) => setForceMajeureClause(e.target.value)} rows={4} />
                    </div>
                    <div className="space-y-2">
                      <Label>Liability Clause</Label>
                      <Textarea value={liabilityClause} onChange={(e) => setLiabilityClause(e.target.value)} rows={4} />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Special Requirements */}
              <AccordionItem value="special">
                <AccordionTrigger>Special Requirements & Notes</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-2">
                    <div className="space-y-2">
                      <Label>Special Requirements</Label>
                      <Textarea 
                        value={specialRequirements} 
                        onChange={(e) => setSpecialRequirements(e.target.value)} 
                        placeholder="Dress code, specific equipment needs, timing requirements..."
                        rows={3} 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Additional Notes</Label>
                      <Textarea 
                        value={additionalNotes} 
                        onChange={(e) => setAdditionalNotes(e.target.value)} 
                        placeholder="Any other information..."
                        rows={3} 
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </ScrollArea>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveContract} disabled={isSaving}>
            {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
            Generate Agreement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
